import { ActionIcon, Box, Group, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import React from 'react';

/** Represents a validated value. */
type Validated<T> = {
    /** Indicates if the value has been modified. */
    dirty: boolean;
    /** Indicates if the value is valid. */
    valid: boolean;
    /** The actual value. */
    value: T;
};

export const KeyValueEditor = ({ onUpdate }: { onUpdate: (payload: Record<string, string>) => void }) => {
    const [payloadRows, setPayloadRows] = React.useState<[Validated<string>, Validated<string>][]>([
        [
            { dirty: false, valid: false, value: '' },
            { dirty: false, valid: false, value: '' },
        ],
    ]);

    // Update parent component when payload changes
    React.useEffect(() => {
        const payload: Record<string, string> = {};

        for (const [key, value] of payloadRows) {
            if (key.valid && value.valid) {
                payload[key.value] = value.value;
            }
        }

        onUpdate(payload);
    }, [payloadRows, onUpdate]);

    // Validate and format rows
    const validateRows = React.useCallback((rows: [Validated<string>, Validated<string>][]) => {
        const trimmedRows = rows.map(
            ([key, value]) =>
                [
                    { ...key, value: key.value.trim() },
                    { ...value, value: value.value.trim() },
                ] as const,
        );

        const validatedRows = trimmedRows.map(([key, value]) => {
            const isDirty = key.dirty || value.dirty;
            const isKeyValid = key.value !== '' && trimmedRows.filter(([k]) => k.value === key.value).length === 1;
            const isValueValid = true; // Values are always valid

            return [
                { ...key, dirty: isDirty, valid: isKeyValid },
                { ...value, dirty: isDirty, valid: isValueValid },
            ] as const;
        });

        return validatedRows as [Validated<string>, Validated<string>][];
    }, []);

    const insertRow = React.useCallback((index: number) => {
        setPayloadRows((prevRows) => {
            const newRows = [...prevRows];
            newRows.splice(index, 0, [
                { dirty: false, valid: false, value: '' },
                { dirty: false, valid: false, value: '' },
            ]);
            return newRows;
        });
    }, []);

    const deleteRow = React.useCallback(
        (index: number) => {
            setPayloadRows((prevRows) => {
                if (prevRows.length <= 1) {
                    // Don't allow deleting the last row, instead reset it
                    return [
                        [
                            { dirty: false, valid: false, value: '' },
                            { dirty: false, valid: false, value: '' },
                        ],
                    ];
                }
                const newRows = [...prevRows];
                newRows.splice(index, 1);
                return validateRows(newRows);
            });
        },
        [validateRows],
    );

    const deleteAllRows = React.useCallback(() => {
        modals.openConfirmModal({
            title: 'Delete all rows',
            children: <Text>Are you sure you want to delete all rows?</Text>,
            labels: { confirm: 'Delete All', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: () => {
                setPayloadRows([
                    [
                        { dirty: false, valid: false, value: '' },
                        { dirty: false, valid: false, value: '' },
                    ],
                ]);
            },
        });
    }, []);

    const updateRowKey = React.useCallback(
        (index: number, key: string) => {
            setPayloadRows((prevRows) => {
                const newRows = [...prevRows];
                const currentRow = newRows[index]!;
                const [currentKey] = currentRow;
                currentKey.dirty = true;
                currentKey.value = key;
                return validateRows(newRows);
            });
        },
        [validateRows],
    );

    const updateRowValue = React.useCallback(
        (index: number, value: string) => {
            setPayloadRows((prevRows) => {
                const newRows = [...prevRows];
                const currentRow = newRows[index]!;
                const [, currentValue] = currentRow;
                currentValue.dirty = true;
                currentValue.value = value;
                return validateRows(newRows);
            });
        },
        [validateRows],
    );

    return (
        <Stack gap="sm">
            {payloadRows.map(([key, value], index) => (
                <Group key={index} gap="sm">
                    <TextInput
                        value={key.value}
                        onChange={(e) => updateRowKey(index, e.target.value)}
                        placeholder="Enter key"
                        error={key.dirty && !key.valid ? 'Duplicate or empty key' : undefined}
                        style={{ flex: 1 }}
                    />
                    <TextInput
                        value={value.value}
                        onChange={(e) => updateRowValue(index, e.target.value)}
                        placeholder="Enter value"
                        error={value.dirty && !value.valid ? true : undefined}
                        style={{ flex: 1 }}
                    />
                    <Group gap={4}>
                        <Tooltip label="Insert row below">
                            <ActionIcon variant="subtle" onClick={() => insertRow(index + 1)}>
                                <IconPlus size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete row">
                            <ActionIcon variant="subtle" color="red" onClick={() => deleteRow(index)}>
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Group>
            ))}

            <Group gap="sm" pt="xs">
                <ActionIcon variant="subtle" onClick={() => insertRow(payloadRows.length)}>
                    <IconPlus size={18} />
                </ActionIcon>
                <Text c="dimmed">Add Row</Text>
                <Box flex={1} />
                <ActionIcon variant="subtle" color="red" onClick={() => deleteAllRows()}>
                    <IconTrash size={18} />
                </ActionIcon>
                <Text c="dimmed">Delete All</Text>
            </Group>
        </Stack>
    );
};
